import React, { useEffect, useState } from "react";

import { useForm } from "../useForm";

import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import {
  classNames,
  generateReceivingLotNumber,
  standardWeightOptions,
} from "../../../shared/utility";
import DollarInputText from "../../controls/inputs/DollarInputText";
import Button from "../../controls/Button";

const initialFValues = {
  product_id: null,
  product_name: "",
  plu: "",
  lot_number: "",
  net_weight: 0,
  tare_weight: 0,
  weight_type: { value: -1 },
  carton_count: 0,
  unit_cost: 0,
  piece_count: 0,
  total_cost: 0,
  warehouse_id: null,
  warehouse_location_id: null,
};

export default function AddItemToReceivingForm(props) {
  const {
    addItemToReceiving,
    isEdit,
    product,
    supplierNumber,
    getWarehouses,
    getWarehouseLocations,
  } = props;

  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [warehouseLocationsOptions, setWarehouseLocationsOptions] = useState(
    []
  );
  const [initialLoadInProgress, setInitialLoadInProgress] = useState(true);
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    const valuesToValidate = { ...values, ...fieldValues };
    const isPieceBased = Number(valuesToValidate.weight_type?.value) === 3;

    if ("lot_number" in fieldValues) {
      temp.lot_number = null;
      if (fieldValues.lot_number === "") {
        temp.lot_number = "This field is required.";
      }
    }

    if ("net_weight" in fieldValues) {
      temp.net_weight = null;
      if (
        !isPieceBased &&
        (fieldValues.net_weight === "" || Number(fieldValues.net_weight) <= 0)
      ) {
        temp.net_weight = "This field is required.";
      }
    }

    if ("tare_weight" in fieldValues) {
      temp.tare_weight = null;
      if (!isPieceBased && fieldValues.tare_weight === "") {
        temp.tare_weight = "This field is required.";
      }
    }

    if ("piece_count" in fieldValues) {
      temp.piece_count = null;
      if (
        isPieceBased &&
        (fieldValues.piece_count === "" || Number(fieldValues.piece_count) <= 0)
      ) {
        temp.piece_count = "This field is required.";
      }
    }

    if ("weight_type" in fieldValues) {
      temp.weight_type = null;
      if (fieldValues.weight_type) {
        if (fieldValues.weight_type.value === -1) {
          temp.weight_type = "This field is required.";
        }
      } else {
        temp.weight_type = "This field is required.";
      }
    }

    if ("carton_count" in fieldValues) {
      temp.carton_count = null;
      if (fieldValues.carton_count === "" || fieldValues.carton_count === 0) {
        temp.carton_count = "This field is required.";
      }
    }

    if ("unit_cost" in fieldValues) {
      temp.unit_cost = null;
      if (fieldValues.unit_cost === "" || fieldValues.unit_cost === 0) {
        temp.unit_cost = "This field is required.";
      }
    }

    if ("warehouse_id" in fieldValues) {
      temp.warehouse_id = null;
      if (fieldValues.warehouse_id) {
        if (fieldValues.warehouse_id.value === -1) {
          temp.warehouse_id = "This field is required.";
        }
      } else {
        temp.warehouse_id = "This field is required.";
      }
    }

    if ("warehouse_location_id" in fieldValues) {
      temp.warehouse_location_id = null;
      if (fieldValues.warehouse_location_id) {
        if (fieldValues.warehouse_location_id.value === -1) {
          temp.warehouse_location_id = "This field is required.";
        }
      } else {
        temp.warehouse_location_id = "This field is required.";
      }
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === null);
  };

  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInputChange,
    handleMultipleInputChange,
  } = useForm(initialFValues, true, validate);

  function getSelectedWarehouse(warehouse_id, warehouses = warehouseOptions) {
    var warehouseSelected = {};
    if (product) {
      if (product.warehouse_id) {
        warehouseSelected = warehouses.find((el) => el.value === warehouse_id);

        if (typeof warehouseSelected === "undefined") {
          warehouseSelected = { value: null };
        }
      }
    }
    return warehouseSelected;
  }

  function getSelectedWarehouseLocation(
    warehouse_location_id,
    wLocations = warehouseLocationsOptions
  ) {
    var warehouseLocationSelected = {};
    if (product) {
      if (product.warehouse_location_id) {
        warehouseLocationSelected = wLocations.find(
          (el) => el.value === warehouse_location_id
        );

        if (typeof warehouseLocationSelected === "undefined") {
          warehouseLocationSelected = { value: null };
        }
      }
    }
    return warehouseLocationSelected;
  }

  const onLoadData = async function () {
    setInitialLoadInProgress(true);
    setInitialLoadFailed(false);

    try {
      const [warehousesResult, warehouseLocationsResult] = await Promise.all([
        getWarehouses(),
        product?.warehouse_id
          ? getWarehouseLocations(product.warehouse_id).catch(() => ({
              success: false,
              data: [],
            }))
          : Promise.resolve({ success: true, data: [] }),
      ]);

      // Soft-fail locations so operators can still pick warehouse/location manually.
      if (!warehousesResult.success) {
        throw new Error("Unable to load receiving item options.");
      }

      const locationOptions = warehouseLocationsResult.success
        ? warehouseLocationsResult.data || []
        : [];

      setWarehouseOptions(warehousesResult.data);
      setWarehouseLocationsOptions(locationOptions);

      const warehouseSelected = getSelectedWarehouse(
        product.warehouse_id,
        warehousesResult.data
      );
      const warehouseLocationSelected = getSelectedWarehouseLocation(
        product.warehouse_location_id,
        locationOptions
      );

      let selectedWeightType = {};
      if (product?.stock_unit_of_measure) {
        if (product.stock_unit_of_measure === 2) {
          selectedWeightType = { value: 3, label: "pieces" };
        } else {
          selectedWeightType = standardWeightOptions.find(
            (el) => el.value === product.stock_unit_of_measure
          );
        }
      }

      if (isEdit) {
        setValues({
          ...values,
          ...product,
          weight_type: selectedWeightType,
          warehouse_id: warehouseSelected,
          warehouse_location_id: warehouseLocationSelected,
        });
      } else {
        setValues({
          ...values,
          weight_type: selectedWeightType,
          product_id: product.id,
          product_name: product.name,
          plu: product.plu,
          unit_cost: product.unit_cost,
          warehouse_id: warehouseSelected,
          warehouse_location_id: warehouseLocationSelected,
          lot_number: generateReceivingLotNumber(supplierNumber, product.plu),
        });
      }
    } catch {
      setInitialLoadFailed(true);
    } finally {
      setInitialLoadInProgress(false);
    }
  };

  useEffect(() => {
    onLoadData();
  }, [product]);

  const [inProgress, setInProgress] = useState(false);
  const isPieceBased = Number(values.weight_type?.value) === 3;

  function calculateTotalCost(amount, unit_cost, carton_count) {
    const total_cost = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount * unit_cost * carton_count);

    return total_cost;
  }

  const handleSubmit = async function () {
    setInProgress(true);
    if (validate()) {
      await addItemToReceiving(values);
    }
    setInProgress(false);
  };

  const onLoadWarehouseLocations = async function (selectedWarehouse) {
    setValues({
      ...values,
      warehouse_id: selectedWarehouse,
      warehouse_location_id: null,
    });
    const result = await getWarehouseLocations(selectedWarehouse.value);
    if (result.success) {
      setWarehouseLocationsOptions(result.data);
    }
  };

  if (initialLoadInProgress) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-64 items-center justify-center gap-3 text-brand"
      >
        <span
          aria-hidden="true"
          className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-accent"
        />
        <span className="font-medium">Loading item details...</span>
      </div>
    );
  }

  if (initialLoadFailed) {
    return (
      <div
        role="alert"
        className="flex min-h-64 flex-col items-center justify-center gap-3"
      >
        <p className="font-medium text-danger">Unable to load item details.</p>
        <Button type="button" onClick={onLoadData}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <form className="space-y-4">
      <div>
        <p className="text-md font-bold">{values.product_name}</p>
        <p className="text-md font-bold">{values.plu}</p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Input
          label="Lot Number"
          name="lot_number"
          required
          onChange={handleInputChange}
          value={values.lot_number}
          labelOn={true}
          type="text"
          disabled={isEdit}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.lot_number}
        />

        <div
          className={classNames(
            "grid col-span-2 gap-4",
            isPieceBased ? "grid-cols-2" : "grid-cols-3"
          )}
        >
          {isPieceBased ? (
            <Input
              label="Piece Count"
              name="piece_count"
              required
              onChange={(e) => {
                handleMultipleInputChange({
                  piece_count: e.target.value,
                  total_cost: calculateTotalCost(
                    e.target.value,
                    values.unit_cost,
                    values.carton_count
                  ),
                });
              }}
              value={values.piece_count}
              error={errors.piece_count}
              labelOn={true}
              type="number"
            />
          ) : (
            <>
              <Input
                label="Net Weight"
                name="net_weight"
                required
                onChange={(e) => {
                  handleMultipleInputChange({
                    net_weight: e.target.value,
                    total_cost: calculateTotalCost(
                      e.target.value,
                      values.unit_cost,
                      values.carton_count
                    ),
                  });
                }}
                value={values.net_weight}
                labelOn={true}
                type="number"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
                error={errors.net_weight}
              />

              <Input
                label="Tare Weight"
                name="tare_weight"
                required
                onChange={handleInputChange}
                value={values.tare_weight}
                labelOn={true}
                type="number"
                disabled={isEdit}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
                error={errors.tare_weight}
              />
            </>
          )}

          <SelectMenu
            handleInputChange={(e) => {
              const chosenUnit = e.target.value.value;
              const newTotalCost = calculateTotalCost(
                chosenUnit === 3 ? values.piece_count : values.net_weight,
                values.unit_cost,
                values.carton_count
              );
              var newPieceCount = values.piece_count;
              if (chosenUnit !== 3) {
                newPieceCount = 0;
              }

              setValues({
                ...values,
                weight_type: e.target.value,
                piece_count: newPieceCount,
                total_cost: newTotalCost,
              });
            }}
            name="weight_type"
            required
            disabled={product.stock_unit_of_measure === 2}
            options={standardWeightOptions}
            value={values.weight_type}
            title={"Weight Unit"}
            error={errors.weight_type}
            showLabel={true}
          />
        </div>
      </div>
      <div
        className="grid grid-cols-3 gap-4"
      >
        <Input
          label="Carton Count"
          name="carton_count"
          required
          onChange={(e) => {
            handleMultipleInputChange({
              carton_count: e.target.value,
              total_cost: calculateTotalCost(
                values.weight_type.value === 3
                  ? values.piece_count
                  : values.net_weight,
                values.unit_cost,
                e.target.value
              ),
            });
          }}
          value={values.carton_count}
          labelOn={true}
          type="number"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.carton_count}
        />

        <DollarInputText
          label="Unit Cost"
          name="unit_cost"
          required
          onChange={(e) => {
            handleMultipleInputChange({
              unit_cost: e.target.value,
              total_cost: calculateTotalCost(
                values.weight_type.value === 3
                  ? values.piece_count
                  : values.net_weight,
                e.target.value,
                values.carton_count
              ),
            });
          }}
          value={values.unit_cost}
          labelOn={true}
          type="number"
          className="mt-1 block w-full focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.unit_cost}
        />

        <Input
          label="Total Cost"
          name="total_cost"
          value={values.total_cost}
          labelOn={true}
          type="text"
          disabled={true}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
          error={errors.total_cost}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <SelectMenu
          handleInputChange={(e) => onLoadWarehouseLocations(e.target.value)}
          name="warehouse_id"
          required
          options={warehouseOptions}
          value={values.warehouse_id}
          title={"Warehouse"}
          error={errors.warehouse_id}
          showLabel={true}
          menuPortalTarget={document.body}
        />

        <SelectMenu
          handleInputChange={handleInputChange}
          name="warehouse_location_id"
          required
          options={warehouseLocationsOptions}
          value={values.warehouse_location_id}
          title={"Location"}
          error={errors.warehouse_location_id}
          showLabel={true}
          menuPortalTarget={document.body}
        />
      </div>
      <Button type="button" onClick={handleSubmit} loading={inProgress}>
        {isEdit ? "Save" : "Add"}
      </Button>
    </form>
  );
}
